import { Injectable } from '@angular/core';

import { environment } from '@environments/environment';

interface TurnstileWidget {
	render: (container: HTMLElement, options: any) => string;
	execute: (widgetId: string) => void;
	remove: (widgetId: string) => void;
}

export class TurnstileVerificationError extends Error {
	constructor(message: string = 'Turnstile verification failed.') {
		super(message);
		this.name = 'TurnstileVerificationError';
	}
}

declare global {
	interface Window {
		turnstile?: TurnstileWidget;
	}
}

@Injectable()
export class TurnstileService {
	private scriptLoading: Promise<void> | undefined;

	public execute(): Promise<string> {
		if (!environment.turnstile_site_key) {
			return Promise.resolve('');
		}

		return this.loadScript()
			.then(() => this.executeChallenge())
			.catch((error: Error) => {
				if (error instanceof TurnstileVerificationError) {
					throw error;
				}

				throw new TurnstileVerificationError(error.message);
			});
	}

	private loadScript(): Promise<void> {
		if (window.turnstile) {
			return Promise.resolve();
		}

		if (this.scriptLoading) {
			return this.scriptLoading;
		}

		this.scriptLoading = new Promise<void>((resolve, reject) => {
			const script = document.createElement('script');
			script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
			script.async = true;
			script.defer = true;
			script.onload = () => resolve();
			script.onerror = () => reject(new Error('Failed to load Turnstile script.'));
			document.head.appendChild(script);
		});

		return this.scriptLoading;
	}

	private executeChallenge(): Promise<string> {
		return new Promise<string>((resolve, reject) => {
			const turnstile = window.turnstile;

			if (!turnstile) {
				reject(new TurnstileVerificationError('Turnstile is unavailable.'));
				return;
			}

			const availableTurnstile: TurnstileWidget = turnstile;

			const container = document.createElement('div');
			container.style.position = 'fixed';
			container.style.left = '-10000px';
			container.style.top = '-10000px';
			document.body.appendChild(container);

			let widgetId: string;
			const cleanup = () => {
				if (widgetId) {
					availableTurnstile.remove(widgetId);
				}
				container.remove();
			};

			widgetId = availableTurnstile.render(container, {
				sitekey: environment.turnstile_site_key,
				execution: 'execute',
				appearance: 'interaction-only',
				callback: (token: string) => {
					cleanup();
					resolve(token);
				},
				'error-callback': () => {
					cleanup();
					reject(new TurnstileVerificationError());
				},
				'expired-callback': () => {
					cleanup();
					reject(new TurnstileVerificationError('Turnstile verification expired.'));
				},
			});

			availableTurnstile.execute(widgetId);
		});
	}
}
